/**
 * Emisor de impresión: datos del negocio que se imprimen, por sucursal.
 *
 * Un tenant puede operar sucursales bajo distintas razones sociales / domicilios.
 * Cada campo de `ubicacion` es un override OPCIONAL del parámetro global equivalente:
 *
 *     trim(ubicacion.<campo>) || trim(parametrosGlobales.afipsdk_<campo>) || fallback
 *
 * Es el ÚNICO lugar donde vive esa regla: el ticket y el PDF mockup la usan desde acá
 * para no divergir (cinco cadenas de fallback copiadas es garantía de divergencia).
 *
 * ⚠️ ALCANCE: esto cambia lo que se IMPRIME, no quién emite.
 *
 *   - El ticket es un documento comercial, no fiscal: puede mostrar los datos de la
 *     sucursal. Es el caso de uso que motivó todo esto.
 *   - El PDF mockup (factura en negro, presupuesto, remito) tampoco es fiscal.
 *   - El PDF de factura AFIP (`facturaAfipHtmlGenerator`) NO usa este módulo a propósito:
 *     lleva CAE y QR oficial, así que su emisor tiene que ser el CUIT que autorizó el
 *     comprobante. Ahí los datos salen de `parametrosGlobales` y sólo de ahí.
 *   - `afipService` (WSFE) tampoco: lee `afipsdk_cuit` + certificado del tenant. En
 *     particular `afipsdk_condicion_iva` decide MAW vs FEEWS al crear punto de venta; el
 *     override de sucursal NO debe llegar ahí, por más que el nombre invite al error.
 *
 * Consecuencia asumida (decisión explícita, no olvido): una sucursal con CUIT propio
 * imprime ese CUIT en el ticket mientras la factura ARCA de la misma venta sale con el
 * CUIT del tenant. Hoy al cliente se le entrega el ticket, no la factura; si un tenant
 * empieza a entregarla, es responsabilidad suya configurar coherente. El emisor legal
 * real (tabla `emisor` + certificados por perfil) está anotado como pendiente.
 */

import axios from 'axios';

const FALLBACK_RAZON_SOCIAL = 'Mi Negocio';

// campo de `ubicacion` → parámetro global que hace de default.
const CAMPOS_EMISOR = {
  razonSocial: 'afipsdk_razon_social',
  domicilio: 'afipsdk_domicilio',
  telefono: 'afipsdk_telefono',
  cuit: 'afipsdk_cuit',
  condicionIva: 'afipsdk_condicion_iva',
};

function getServerURL() {
  const MODE = process.env.REACT_APP_MODE;
  const baseURL = MODE === 'production'
    ? process.env.REACT_APP_URL_PROD
    : process.env.REACT_APP_URL_DEV;
  return (baseURL || 'http://localhost:5000').replace(/\/$/, '');
}

/** "", "   ", null y undefined son todos "sin cargar". */
function limpiar(valor) {
  if (valor === null || valor === undefined) return null;
  const texto = String(valor).trim();
  return texto === '' ? null : texto;
}

// Cache de ubicaciones para no pegarle a la API en cada impresión. Se invalida al
// crear/editar/borrar una sucursal (ErpContext) para que el cambio se vea sin F5.
let cacheUbicaciones = null;
let cachePendiente = null;

export function invalidarCacheUbicaciones() {
  cacheUbicaciones = null;
  cachePendiente = null;
}

/** Deja la lista de ubicaciones en cache; la comparte si hay un fetch en vuelo. */
async function getUbicacionesCache() {
  if (cacheUbicaciones) return cacheUbicaciones;
  if (!cachePendiente) {
    cachePendiente = axios
      .get(`${getServerURL()}/adminAPI/getUbicacion`)
      .then((response) => {
        cacheUbicaciones = Array.isArray(response.data) ? response.data : [];
        return cacheUbicaciones;
      })
      .catch((error) => {
        console.warn('[emisorImpresion] no se pudieron obtener las ubicaciones:', error?.message || error);
        return [];
      })
      .finally(() => {
        cachePendiente = null;
      });
  }
  return cachePendiente;
}

/**
 * Fila de la sucursal, o `null` si no se puede resolver (→ todo cae al global).
 * @param {number|string|null} idUbicacion
 * @param {Array} [ubicacionesDisponibles] lista ya cargada (ej. la de ErpContext) para evitar el fetch
 */
async function buscarUbicacion(idUbicacion, ubicacionesDisponibles = null) {
  if (idUbicacion === null || idUbicacion === undefined || idUbicacion === '') return null;

  const id = Number(idUbicacion);
  if (!Number.isFinite(id)) return null;

  try {
    const lista = Array.isArray(ubicacionesDisponibles) && ubicacionesDisponibles.length
      ? ubicacionesDisponibles
      : await getUbicacionesCache();

    // getUbicacion filtra eliminadas: una sucursal borrada no se encuentra y cae al global.
    return lista.find((u) => Number(u?.id) === id) || null;
  } catch (error) {
    console.warn('[emisorImpresion] error resolviendo la ubicación:', error?.message || error);
    return null;
  }
}

/**
 * Datos del emisor a imprimir: override de la sucursal → parámetro global → `null`.
 *
 * `null` significa "no hay dato": el llamador decide si omite la línea (ticket) o pone
 * un placeholder (el mockup, que ya tenía uno y se conserva).
 *
 * `razonSocial` es la única con fallback final no nulo (`'Mi Negocio'`, comportamiento
 * histórico del ticket), y se puede cambiar con `opciones.fallbackRazonSocial`.
 *
 * @param {number|string|null} idUbicacion sucursal GRABADA en la transacción (no la cookie de impresora)
 * @param {Object} parametrosGlobales objeto con los `afipsdk_*` ya cargados
 * @param {Object} [opciones]
 * @param {Array}  [opciones.ubicacionesDisponibles] lista ya cargada, evita el fetch
 * @param {string|null} [opciones.fallbackRazonSocial]
 * @returns {Promise<{razonSocial: string|null, domicilio: string|null, telefono: string|null, cuit: string|null, condicionIva: string|null}>}
 */
export async function resolverEmisorImpresion(idUbicacion, parametrosGlobales, opciones = {}) {
  const {
    ubicacionesDisponibles = null,
    fallbackRazonSocial = FALLBACK_RAZON_SOCIAL,
  } = opciones;

  const ubicacion = await buscarUbicacion(idUbicacion, ubicacionesDisponibles);

  const emisor = {};
  for (const [campo, parametroGlobal] of Object.entries(CAMPOS_EMISOR)) {
    emisor[campo] = limpiar(ubicacion?.[campo]) || limpiar(parametrosGlobales?.[parametroGlobal]) || null;
  }
  if (!emisor.razonSocial) emisor.razonSocial = limpiar(fallbackRazonSocial);

  return emisor;
}
